/**
 * Pure helpers for the online payments screens (tracker item 38).
 */
import * as types from '../../api/types';

/** Problems with a typed amount against [min, max] (null = fine). Amounts are strings with up to 2 dp. */
export function amountError(raw: string, min: string, max: string): string | null {
  const value = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return 'Enter an amount like 25 or 25.50';
  const cents = Math.round(Number(value) * 100);
  const minCents = Math.round(Number(min) * 100);
  const maxCents = Math.round(Number(max) * 100);
  if (cents <= 0) return 'The amount must be more than zero';
  if (cents > maxCents) return `At most ${formatUsd(max)} (the balance due)`;
  if (cents < minCents) return `At least ${formatUsd(min)}`;
  return null;
}

export function formatUsd(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function cardLabel(card: { brand?: string | null; last4?: string | null } | null | undefined): string {
  if (!card) return 'Card';
  const brand = (card.brand || 'Card').replace(/_/g, ' ');
  return card.last4 ? `${brand} ending ${card.last4}` : brand;
}

export function expiryLabel(m: Pick<types.CustomerPaymentMethod, 'expMonth' | 'expYear'>): string {
  return m.expMonth && m.expYear ? `${String(m.expMonth).padStart(2, '0')}/${m.expYear}` : '';
}

export const ATTEMPT_STATUS_LABELS: Record<types.OnlinePaymentAttemptStatus, string> = {
  PENDING: 'Processing',
  CHARGED: 'Charged (recording)',
  RECORDED: 'Paid',
  DECLINED: 'Declined',
  ERROR: 'Outcome unknown',
  RECORD_FAILED: 'Charged, not recorded',
};

export const ATTEMPT_SOURCE_LABELS: Record<types.OnlinePaymentSource, string> = {
  PUBLIC_LINK: 'Pay link',
  AUTOPAY: 'Autopay',
  STAFF: 'Card on file',
  PORTAL: 'Customer portal',
};

export function attemptStatusClass(status: types.OnlinePaymentAttemptStatus): string {
  switch (status) {
    case 'RECORDED':
      return 'bg-green-100 text-green-800';
    case 'DECLINED':
      return 'bg-gray-100 text-gray-700';
    case 'RECORD_FAILED':
    case 'ERROR':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
}

/** Why an attempt needs someone (shown next to it), or null. */
export function attentionReason(a: types.OnlinePaymentAttempt): string | null {
  if (a.status === 'RECORD_FAILED') return 'The card was charged but the payment is not recorded: reconcile it.';
  if (a.status === 'ERROR') return 'No answer from the processor: check the processor dashboard, then reconcile or close it as not charged.';
  if (a.paymentStatus === 'ACTIVE' && a.paymentProcessorStatus === 'REFUNDED_EXTERNALLY') return 'Refunded in the processor dashboard: finish the refund to void the payment.';
  if (a.paymentStatus === 'ACTIVE' && a.paymentProcessorStatus === 'REFUNDED') return 'Refunded, but the void did not finish: finish the refund.';
  if (a.needsReview) return a.reviewNote ?? 'Needs review.';
  if (a.status === 'PENDING' && a.needsAttention) return 'Still processing after 15 minutes: reconcile it.';
  return null;
}

/** The refund can be finished (void pending) without a second refund at the processor. */
export function refundPendingVoid(a: types.OnlinePaymentAttempt): boolean {
  return a.paymentStatus === 'ACTIVE' && (a.paymentProcessorStatus === 'REFUNDED' || a.paymentProcessorStatus === 'REFUNDED_EXTERNALLY');
}

export function isOnlinePayment(p: Pick<types.Payment, 'processor'>): boolean {
  return !!p.processor;
}

/** The OAuth callback flag on /settings/payments (?connect=success|error&reason=...) as a message. */
export function connectResultMessage(search: string): { ok: boolean; message: string } | null {
  const params = new URLSearchParams(search);
  const result = params.get('connect');
  if (result === 'success') return { ok: true, message: 'The payment processor is connected.' };
  if (result !== 'error') return null;
  const reasons: Record<string, string> = {
    denied: 'The connection was cancelled at the processor.',
    invalid_state: 'The connection link expired or was already used. Start again.',
    account_in_use: 'That processor account is already connected to another company.',
    connect_failed: 'The processor did not accept the connection. Try again.',
    not_configured: 'The server is not set up to store processor credentials.',
    invalid_request: 'The connection could not be completed. Start again.',
  };
  return { ok: false, message: reasons[params.get('reason') ?? ''] ?? 'The connection could not be completed.' };
}

/** "0, 3, 7" -> [0, 3, 7], or an error message. */
export function parseRetryDays(raw: string): number[] | string {
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0 || parts.length > 6 || !parts.every((p) => /^\d{1,2}$/.test(p))) return 'Up to 6 whole numbers of days, separated by commas';
  const days = parts.map(Number);
  if (days[0] !== 0) return 'The first attempt is day 0';
  if (!days.every((d, i) => i === 0 || d > days[i - 1])) return 'Days must go up';
  if (days.some((d) => d > 60)) return 'At most 60 days';
  return days;
}

export function newIdempotencyKey(): string {
  return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
